import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Markdown } from "@/components/ui/markdown";
import { Seo } from "@/components/Seo";
import { ProjectIcon, isImageIcon } from "@/components/ProjectIcon";
import type { ProjectWithDomains } from "@/lib/types";

export function ProjectDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [project, setProject] = useState<ProjectWithDomains | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`/api/projects/${slug}`)
      .then((r) => {
        if (!r.ok) { setNotFound(true); return null; }
        return r.json();
      })
      .then((data) => data && setProject(data));
  }, [slug]);

  if (notFound) {
    return (
      <div className="container mx-auto px-6 py-16 text-center">
        <Seo title="Project Not Found" description="This project couldn't be found." path={`/projects/${slug}`} noindex />
        <div className="text-6xl mb-4">🦆💨</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mb-4">Project Not Found</h1>
        <p className="text-muted-foreground mb-6">This project seems to have flown away.</p>
        <Button asChild variant="outline">
          <Link to="/projects">Back to Projects</Link>
        </Button>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container mx-auto px-6 py-16 text-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const hasLinks = project.website_url || project.store_url_ios || project.store_url_android;

  // Only image icons make sense as an og:image; emoji glyphs don't. Scrapers
  // want an absolute URL, so resolve relative asset paths against the site.
  const ogImage = isImageIcon(project.icon_url)
    ? project.icon_url.startsWith("http")
      ? project.icon_url
      : `https://loosegoosesystems.com${project.icon_url}`
    : undefined;

  return (
    <div className="container mx-auto px-6 py-16 max-w-3xl">
      <Seo
        title={project.name}
        description={project.tagline}
        path={`/projects/${project.slug}`}
        image={ogImage}
      />
      <Link to="/projects" className="text-sm text-muted-foreground hover:text-foreground mb-8 inline-flex items-center gap-1 transition-colors">
        &larr; Back to Projects
      </Link>
      <div className="mt-6">
        <ProjectIcon icon={project.icon_url} size="text-6xl" className="mb-4" alt={project.name} />
        <h1 className="font-display text-4xl font-extrabold tracking-tight mb-2">{project.name}</h1>
        <p className="text-xl text-muted-foreground mb-4">{project.tagline}</p>

        {project.domains.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-8">
            {project.domains.map((d) => (
              <Link key={d.id} to={`/domains/${d.slug}`}>
                <Badge domainSlug={d.slug} className="hover:opacity-80 cursor-pointer">
                  {d.icon && <span className="mr-1">{d.icon}</span>}
                  {d.name}
                </Badge>
              </Link>
            ))}
          </div>
        )}

        {hasLinks && (
          <div className="flex flex-wrap gap-3 mb-10">
            {project.website_url && (
              <Button asChild className="rounded-full bg-ember text-ember-foreground hover:bg-ember/90">
                <a href={project.website_url} target="_blank" rel="noopener noreferrer">Visit Website</a>
              </Button>
            )}
            {project.store_url_ios && (
              <Button asChild className="rounded-full bg-ember text-ember-foreground hover:bg-ember/90">
                <a href={project.store_url_ios} target="_blank" rel="noopener noreferrer">App Store</a>
              </Button>
            )}
            {project.store_url_android && (
              <Button asChild className="rounded-full bg-ember text-ember-foreground hover:bg-ember/90">
                <a href={project.store_url_android} target="_blank" rel="noopener noreferrer">Google Play</a>
              </Button>
            )}
          </div>
        )}

        <Markdown source={project.description} />
      </div>
    </div>
  );
}
